import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, peso, fecha, hoy } from '../utils/formato';
import { KILOS_POR_BULTO, aUnidadDeGasto, enUnidadDeCompra } from '../utils/insumo';
import { porSitio, cuadra, dondeFalta } from '../utils/bodega';

// Cuajo, sal, empaque.
//
// La existencia no sale de una columna guardada: se calcula restando lo
// comprado menos lo usado en las cuajadas. Una columna que alguien va sumando y
// restando se desincroniza el primer día que se corrija una compra vieja, y
// desde ahí nadie sabe cuál número es el bueno.
//
// El costo por unidad es un PROMEDIO PONDERADO de todo lo comprado, no el
// precio de la última compra: si el cuajo subió de 40.000 a 60.000 y todavía
// queda del viejo, costear a 60.000 infla el costo del queso que se hizo con
// el barato.
function Insumos() {
  const [insumos, setInsumos] = useState([]);
  const [compras, setCompras] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [nuevo, setNuevo] = useState(null);
  const [comprando, setComprando] = useState(null);
  const [enviando, setEnviando] = useState(null);
  const [centros, setRutas] = useState([]);
  const [porCentro, setPorCentro] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [editando, setEditando] = useState(null);       // la ficha del insumo
  const [corrigiendo, setCorrigiendo] = useState(null); // una compra

  const guardarInsumo = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await axios.put(`/api/insumos/${editando.id}`,
        { nombre: editando.nombre, unidad: editando.unidad,
          unidad_compra: editando.unidad_compra || null,
          equivale: parseFloat(editando.equivale) || null });
      setEditando(null);
      cargar();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo corregir' });
    }
    setGuardando(false);
  };

  const borrarInsumo = async (i) => {
    if (!window.confirm(
      `¿Borrar «${i.nombre}»?\n\nSolo se puede si nunca se ha comprado ni usado.`
    )) return;
    try {
      await axios.delete(`/api/insumos/${i.id}`);
      cargar();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo borrar' });
    }
  };

  const guardarCompra = async (e) => {
    e.preventDefault();
    const cant = parseFloat(corrigiendo.cantidad);
    const costo = parseFloat(corrigiendo.costo_total);
    if (!(cant > 0) || !(costo > 0)) {
      return alert('La cantidad y el costo deben ser mayores que cero');
    }
    setGuardando(true);
    try {
      await axios.put(`/api/compras-insumos/${corrigiendo.id}`, {
        cantidad: cant, costo_total: costo,
        fecha: corrigiendo.fecha, proveedor: corrigiendo.proveedor || null,
      });
      setCorrigiendo(null);
      cargar();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo corregir' });
    }
    setGuardando(false);
  };

  const borrarCompra = async (c) => {
    if (!window.confirm(
      '¿Borrar esta compra?\n\nLa existencia y el costo por unidad se vuelven a ' +
      'calcular sin ella.'
    )) return;
    try {
      await axios.delete(`/api/compras-insumos/${c.id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo borrar');
    }
  };

  const cargar = useCallback(() => {
    setCargando(true);
    Promise.all([
      axios.get('/api/insumos/resumen').then(r => setInsumos(r.data || [])),
      axios.get('/api/compras-insumos').then(r => setCompras(r.data || [])),
      axios.get('/api/centros').then(r => setRutas(r.data || [])),
      axios.get('/api/insumos/por-centro').then(r => setPorCentro(r.data || [])),
    ]).catch(() => {}).finally(() => setCargando(false));
  }, []);

  useEffect(cargar, [cargar]);

  const valorEnBodega = insumos.reduce(
    (s, i) => s + Math.max(0, i.existencia) * i.costo_unitario, 0
  );
  const invertido = insumos.reduce((s, i) => s + i.invertido, 0);

  const nombreDe = (id) => (insumos.find(i => i.id === id) || {}).nombre || '—';

  const crear = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await axios.post('/api/insumos', {
        ...nuevo,
        unidad_compra: nuevo.unidad_compra || null,
        equivale: parseFloat(nuevo.equivale) || null,
      });
      setNuevo(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar');
    }
    setGuardando(false);
  };

  // Lo que se escribe está en la unidad de COMPRA —bultos— y lo que se guarda
  // en la de gasto —kilos—. La conversión se hace aquí y no la teclea nadie:
  // era la resta de cabeza que metía ceros de más al inventario.
  const enGasto = (insumo, cuanto) =>
    insumo && insumo.unidad_compra && insumo.equivale > 0
      ? aUnidadDeGasto(cuanto, insumo.equivale)
      : cuanto;

  const enviar = async (e) => {
    e.preventDefault();
    const escrito = parseFloat(enviando.cantidad);
    const cant = enGasto(enviando.insumo, escrito);
    if (!(escrito > 0)) return alert('La cantidad debe ser mayor que cero');
    if (!enviando.centro_id) return alert('Elija a cuál ruta');
    setGuardando(true);
    try {
      const { data } = await axios.post('/api/envios-insumos', {
        insumo_id: enviando.insumo.id,
        centro_id: parseInt(enviando.centro_id),
        fecha: enviando.fecha,
        cantidad: cant,
        nota: enviando.nota || null,
      });
      const centro = centros.find(c => String(c.id) === String(enviando.centro_id));
      setAviso({ tipo: 'bueno',
        texto: `${peso(cant, enviando.insumo.unidad)} de ${enviando.insumo.nombre} ` +
               `van para ${centro ? centro.nombre : 'la ruta'}. ` +
               `En la bodega quedan ${peso(data.queda_en_bodega, enviando.insumo.unidad)}. ` +
               'Todavía no es gasto: se vuelve gasto cuando entre a una tina.' });
      setEnviando(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar el envío');
    }
    setGuardando(false);
  };

  const comprar = async (e) => {
    e.preventDefault();
    const escrito = parseFloat(comprando.cantidad);
    const cant = enGasto(comprando.insumo, escrito);
    const costo = parseFloat(comprando.costo_total);
    if (!(escrito > 0)) return alert('La cantidad debe ser mayor que cero');
    if (!(costo > 0)) return alert('El costo debe ser mayor que cero');

    setGuardando(true);
    try {
      await axios.post('/api/compras-insumos', {
        insumo_id: comprando.insumo.id,
        fecha: comprando.fecha,
        cantidad: cant,
        costo_total: costo,
        proveedor: comprando.proveedor || null,
        nota: comprando.nota || null,
      });
      setAviso({
        tipo: 'bueno',
        texto: (comprando.insumo.unidad_compra
                 ? `${peso(escrito, comprando.insumo.unidad_compra)} = `
                 : '') +
               `${peso(cant, comprando.insumo.unidad)} de ${comprando.insumo.nombre} ` +
               `por ${plata(costo)} — sale a ${plata(costo / cant)} por ${comprando.insumo.unidad}`,
      });
      setComprando(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar la compra');
    }
    setGuardando(false);
  };

  // Cuánto costaría la unidad con esta compra, contra lo que costaba antes.
  // Sirve para ver si el proveedor subió el precio.
  const comparacion = () => {
    if (!comprando) return null;
    // Convertida: si se escriben bultos y aquí se dividiera por bultos, el
    // «costo por kilo» saldría 33 veces más alto y parecería que el proveedor
    // subió el precio de golpe.
    const cant = enGasto(comprando.insumo, parseFloat(comprando.cantidad));
    const costo = parseFloat(comprando.costo_total);
    if (!(cant > 0) || !(costo > 0)) return null;
    const nuevoU = costo / cant;
    const viejoU = comprando.insumo.costo_unitario;
    if (!viejoU) return { nuevoU, viejoU: null, subio: null };
    return { nuevoU, viejoU, subio: (nuevoU - viejoU) / viejoU * 100 };
  };
  const comp = comparacion();

  return (
    <>
      <div className="encabezado">
        <h1>Insumos</h1>
        <p>Cuajo, sal, empaque: lo que hay y lo que cuesta</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      <div className="rejilla">
        <div className="dato" style={{ '--color': 'var(--acento)' }}>
          <div className="rotulo">📦 Valor en bodega</div>
          <div className="valor cifra">{plata(valorEnBodega)}</div>
          <div className="pie">lo que queda, al costo promedio</div>
        </div>
        <div className="dato" style={{ '--color': 'var(--acento)' }}>
          <div className="rotulo">💰 Comprado en total</div>
          <div className="valor cifra">{plata(invertido)}</div>
          <div className="pie">desde que se empezó a registrar</div>
        </div>
      </div>

      <div className="bloque">
        <h2>
          📦 Lo que hay
          <span className="contador">{insumos.length}</span>
          <button className="boton chico" style={{ marginLeft: 'auto' }}
                  onClick={() => setNuevo({ nombre: '', unidad: 'kg',
                                            unidad_compra: '', equivale: '' })}>
            ➕ Nuevo insumo
          </button>
        </h2>

        {cargando ? (
          <div className="vacio">Cargando…</div>
        ) : insumos.length === 0 ? (
          <div className="vacio">Todavía no hay insumos registrados.</div>
        ) : (
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Insumo</th>
                  <th style={{ textAlign: 'right' }}>Comprado</th>
                  <th style={{ textAlign: 'right' }}>Usado</th>
                  <th style={{ textAlign: 'right' }}>Queda</th>
                  <th style={{ textAlign: 'right' }}>Costo unidad</th>
                  <th style={{ textAlign: 'right' }}>Valor</th>
                  <th>Última compra</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {insumos.map(i => {
                  const queda = i.existencia;
                  const seAcaba = i.comprado > 0 && queda <= i.comprado * 0.15;
                  return (
                    <tr key={i.id}>
                      <td className="recio">
                        {i.nombre}
                        <div style={{ fontSize: 11, color: 'var(--tinta-tenue)', fontWeight: 400 }}>
                          por {i.unidad}
                          {i.unidad_compra && i.equivale > 0 &&
                            ` · se compra por ${i.unidad_compra} de ${i.equivale} ${i.unidad}`}
                        </div>
                      </td>
                      <td className="num">{i.comprado > 0 ? peso(i.comprado, i.unidad) : '—'}</td>
                      <td className="num">{i.usado > 0 ? peso(i.usado, i.unidad) : '—'}</td>
                      <td className="num recio"
                          style={{ color: queda <= 0 ? 'var(--malo)'
                                 : seAcaba ? 'var(--ojo)' : 'var(--bueno)' }}>
                        {peso(queda, i.unidad)}
                        {/* Y en bultos, que es la unidad en la que se decide
                            cuántos pedir. Los kilos dicen si alcanza la cuajada
                            de mañana; los bultos, qué se carga del camión. */}
                        {queda > 0 && i.unidad_compra && i.equivale > 0 && (
                          <div style={{ fontSize: 11, fontWeight: 400,
                                        color: 'var(--tinta-tenue)' }}>
                            {enUnidadDeCompra(queda, i.equivale)} {i.unidad_compra}
                            {enUnidadDeCompra(queda, i.equivale) === 1 ? '' : 's'}
                          </div>
                        )}
                        {/* Cuánto de ese total está en la bodega. La existencia
                            global puede decir «quedan 5 bultos» estando los
                            cinco a dos horas de la ruta que se quedó sin sal
                            esta mañana. */}
                        {i.enviado > 0 && (
                          <div style={{ fontSize: 11, fontWeight: 400,
                                        color: 'var(--tinta-tenue)' }}>
                            {peso(i.en_bodega, i.unidad)} en bodega
                          </div>
                        )}
                        {queda <= 0 && i.comprado > 0 && (
                          <div style={{ fontSize: 11, fontWeight: 400 }}>se acabó</div>
                        )}
                        {seAcaba && queda > 0 && (
                          <div style={{ fontSize: 11, fontWeight: 400 }}>queda poco</div>
                        )}
                      </td>
                      <td className="num">
                        {i.costo_unitario > 0 ? plata(i.costo_unitario) : '—'}
                        {i.costo_unitario > 0 && (
                          <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>promedio</div>
                        )}
                      </td>
                      <td className="num">
                        {queda > 0 ? plata(queda * i.costo_unitario) : '—'}
                      </td>
                      <td>{i.ultima_compra ? fecha(i.ultima_compra) : '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="boton chico" style={{ marginRight: 4 }}
                                onClick={() => setComprando({
                                  insumo: i, fecha: hoy(), cantidad: '',
                                  costo_total: '', proveedor: '', nota: '',
                                })}>
                          Comprar
                        </button>
                        {/* Enviar a una ruta. Solo aparece si hay centros y si
                            hay algo en la bodega: un botón que siempre contesta
                            «no queda nada» es un botón que estorba. */}
                        {centros.length > 0 && i.en_bodega > 0 && (
                          <button className="boton chico suave" style={{ marginRight: 4 }}
                                  onClick={() => setEnviando({
                                    insumo: i, fecha: hoy(), cantidad: '',
                                    centro_id: centros.length === 1 ? String(centros[0].id) : '',
                                    nota: '',
                                  })}>
                            Enviar
                          </button>
                        )}
                        <button className="boton chico suave" title="Corregir"
                                style={{ marginRight: 4 }}
                                onClick={() => setEditando({
                                  id: i.id, nombre: i.nombre, unidad: i.unidad,
                                  unidad_compra: i.unidad_compra || '',
                                  equivale: i.equivale || '',
                                  tieneHistoria: i.comprado > 0 || i.usado > 0,
                                })}>
                          ✏️
                        </button>
                        <button className="boton chico suave" title="Borrar"
                                onClick={() => borrarInsumo(i)}>
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="cuerpo" style={{ paddingTop: 14 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--tinta-tenue)' }}>
            Lo que «queda» se calcula restando lo usado en las cuajadas a lo comprado.
            Si nunca anotas qué insumos lleva cada cuajada, la columna de «usado» se
            queda en cero y la de «queda» va a mentir hacia arriba.
          </p>
        </div>
      </div>

      {/* Nuevo insumo */}
      {nuevo && (
        <div className="bloque">
          <h2>➕ Nuevo insumo</h2>
          <form className="cuerpo" onSubmit={crear}>
            <div className="campos">
              <label className="campo">
                <span>Nombre *</span>
                <input value={nuevo.nombre} required placeholder="Cuajo, sal, empaque…"
                       onChange={e => setNuevo({ ...nuevo, nombre: e.target.value })} />
              </label>
              <label className="campo">
                <span>Se mide en</span>
                <select value={nuevo.unidad}
                        onChange={e => setNuevo({ ...nuevo, unidad: e.target.value })}>
                  <option value="kg">Kilos</option>
                  <option value="g">Gramos</option>
                  <option value="L">Litros</option>
                  <option value="ml">Mililitros</option>
                  {/* El cuajo de este cliente se mide con la regla del frasco,
                      en centímetros. No es una unidad de peso ni de volumen,
                      pero es la que él usa — y el programa habla como habla
                      quien lo usa, no como habla un laboratorio. */}
                  <option value="cm">Centímetros (regla del frasco)</option>
                  <option value="unidad">Unidades</option>
                </select>
              </label>

              {/* EN QUÉ SE COMPRA, que casi nunca es en lo que se gasta.
                  La sal se compra por bultos de 33 kilos y se gasta en kilos.
                  Antes había que escribir 99 en un campo que decía «kilos»
                  teniendo tres bultos en la mano, y multiplicar de cabeza a las
                  cinco de la mañana es como entra un cero de más. */}
              <label className="campo">
                <span>Se compra por</span>
                <input value={nuevo.unidad_compra} placeholder="bulto, frasco… (opcional)"
                       onChange={e => setNuevo({ ...nuevo, unidad_compra: e.target.value })} />
              </label>
              {nuevo.unidad_compra && (
                <label className="campo">
                  <span>
                    Cuántos {nuevo.unidad} tiene un {nuevo.unidad_compra}
                  </span>
                  <input type="number" step="0.01" min="0" value={nuevo.equivale}
                         placeholder={nuevo.unidad === 'kg' ? String(KILOS_POR_BULTO) : ''}
                         onChange={e => setNuevo({ ...nuevo, equivale: e.target.value })} />
                </label>
              )}
            </div>

            {nuevo.unidad_compra && parseFloat(nuevo.equivale) > 0 && (
              <div className="aviso">
                Al comprar se escriben <strong>{nuevo.unidad_compra}s</strong> y el
                programa guarda los {nuevo.unidad}: 3 {nuevo.unidad_compra}s son{' '}
                <strong>{aUnidadDeGasto(3, nuevo.equivale)} {nuevo.unidad}</strong>.
              </div>
            )}

            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setNuevo(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>
                {guardando ? 'Guardando…' : '💾 Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Corregir el insumo */}
      {editando && (
        <div className="bloque">
          <h2>✏️ Corregir {editando.nombre}</h2>
          <form className="cuerpo" onSubmit={guardarInsumo}>
            {/* La unidad se bloquea si ya hay compras o usos. Cambiar «ml» por
                «kg» en un cuajo que ya tiene historia convertiría 1.000 ml en
                1.000 kg, y el costo por unidad se dividiría por mil sin que
                nada lo dijera. */}
            {editando.tieneHistoria && (
              <div className="aviso ojo">
                Este insumo ya tiene compras o usos anotados en{' '}
                <strong>{editando.unidad}</strong>, así que la unidad no se puede
                cambiar: todas esas cantidades pasarían a significar otra cosa.
                El nombre sí.
              </div>
            )}
            <div className="campos">
              <label className="campo">
                <span>Nombre *</span>
                <input value={editando.nombre} required autoFocus
                       onChange={e => setEditando({ ...editando, nombre: e.target.value })} />
              </label>
              <label className="campo">
                <span>Se mide en</span>
                <select value={editando.unidad} disabled={editando.tieneHistoria}
                        onChange={e => setEditando({ ...editando, unidad: e.target.value })}>
                  <option value="kg">Kilos</option>
                  <option value="g">Gramos</option>
                  <option value="L">Litros</option>
                  <option value="ml">Mililitros</option>
                  <option value="cm">Centímetros (regla del frasco)</option>
                  <option value="unidad">Unidades</option>
                </select>
              </label>

              {/* La unidad de COMPRA sí se puede cambiar aunque haya historia:
                  no toca ni una cantidad guardada, solo cambia en qué unidad se
                  va a preguntar la próxima compra. */}
              <label className="campo">
                <span>Se compra por</span>
                <input value={editando.unidad_compra} placeholder="bulto, frasco… (opcional)"
                       onChange={e => setEditando({ ...editando, unidad_compra: e.target.value })} />
              </label>
              {editando.unidad_compra && (
                <label className="campo">
                  <span>Cuántos {editando.unidad} tiene un {editando.unidad_compra}</span>
                  <input type="number" step="0.01" min="0" value={editando.equivale}
                         placeholder={editando.unidad === 'kg' ? String(KILOS_POR_BULTO) : ''}
                         onChange={e => setEditando({ ...editando, equivale: e.target.value })} />
                </label>
              )}
            </div>
            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setEditando(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>💾 Guardar</button>
            </div>
          </form>
        </div>
      )}

      {/* Corregir una compra */}
      {corrigiendo && (
        <div className="bloque">
          <h2>✏️ Corregir la compra de {corrigiendo.insumo}</h2>
          <form className="cuerpo" onSubmit={guardarCompra}>
            <div className="aviso">
              El costo por unidad y la existencia se vuelven a calcular con lo
              que deje aquí. Las cuajadas ya cerradas <strong>no cambian</strong>:
              cada una guardó lo que costó el día que se hizo.
            </div>
            <div className="campos">
              <label className="campo">
                <span>Cantidad *</span>
                <input type="number" step="0.01" min="0" className="peso" required autoFocus
                       value={corrigiendo.cantidad}
                       onChange={e => setCorrigiendo({ ...corrigiendo, cantidad: e.target.value })} />
              </label>
              <label className="campo">
                <span>Cuánto costó en total *</span>
                <input type="number" step="1" min="0" className="peso" required
                       value={corrigiendo.costo_total}
                       onChange={e => setCorrigiendo({ ...corrigiendo, costo_total: e.target.value })} />
              </label>
              <label className="campo">
                <span>Fecha</span>
                <input type="date" value={corrigiendo.fecha}
                       onChange={e => setCorrigiendo({ ...corrigiendo, fecha: e.target.value })} />
              </label>
              <label className="campo">
                <span>A quién</span>
                <input value={corrigiendo.proveedor} placeholder="Opcional"
                       onChange={e => setCorrigiendo({ ...corrigiendo, proveedor: e.target.value })} />
              </label>
            </div>
            {parseFloat(corrigiendo.cantidad) > 0 && parseFloat(corrigiendo.costo_total) > 0 && (
              <div className="aviso" style={{ marginBottom: 0 }}>
                Queda en{' '}
                <strong className="cifra">
                  {plata(parseFloat(corrigiendo.costo_total) / parseFloat(corrigiendo.cantidad))}
                </strong> por unidad
              </div>
            )}
            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setCorrigiendo(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>💾 Guardar</button>
            </div>
          </form>
        </div>
      )}

      {/* Comprar */}
      {/* Mandar insumo a un ruta */}
      {enviando && (
        <div className="bloque">
          <h2>🚚 Enviar {enviando.insumo.nombre} a una ruta</h2>
          <form className="cuerpo" onSubmit={enviar}>
            <div className="aviso">
              En la bodega hay{' '}
              <strong>{peso(enviando.insumo.en_bodega, enviando.insumo.unidad)}</strong>
              {enviando.insumo.unidad_compra && enviando.insumo.equivale > 0 &&
                ` (${enUnidadDeCompra(enviando.insumo.en_bodega, enviando.insumo.equivale)} ` +
                `${enviando.insumo.unidad_compra}s)`}.
            </div>
            <div className="campos">
              <label className="campo">
                <span>¿A cuál ruta? *</span>
                <select value={enviando.centro_id} required
                        onChange={e => setEnviando({ ...enviando, centro_id: e.target.value })}>
                  <option value="">— Elegir —</option>
                  {centros.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </label>
              <label className="campo">
                <span>
                  Cuántos {enviando.insumo.unidad_compra
                    ? enviando.insumo.unidad_compra + 's'
                    : enviando.insumo.unidad} *
                </span>
                <input type="number" step="0.01" min="0" className="peso" required autoFocus
                       value={enviando.cantidad}
                       onChange={e => setEnviando({ ...enviando, cantidad: e.target.value })} />
              </label>
              <label className="campo">
                <span>Fecha</span>
                <input type="date" value={enviando.fecha}
                       onChange={e => setEnviando({ ...enviando, fecha: e.target.value })} />
              </label>
              <label className="campo">
                <span>Nota</span>
                <input value={enviando.nota} placeholder="Quién lo llevó, opcional"
                       onChange={e => setEnviando({ ...enviando, nota: e.target.value })} />
              </label>
            </div>

            {enviando.insumo.unidad_compra && enviando.insumo.equivale > 0 &&
             parseFloat(enviando.cantidad) > 0 && (
              <div className="aviso">
                {peso(parseFloat(enviando.cantidad), enviando.insumo.unidad_compra)}
                {' = '}
                <strong>
                  {peso(enGasto(enviando.insumo, parseFloat(enviando.cantidad)),
                        enviando.insumo.unidad)}
                </strong>
                {' '}que salen de la bodega.
              </div>
            )}

            {/* Lo que un envío NO es. Sin decirlo, quien lo anota espera ver el
                gasto en Contabilidad esa misma noche y no lo va a encontrar. */}
            <p style={{ fontSize: 13, color: 'var(--tinta-tenue)', margin: '10px 0 0' }}>
              Un envío <strong>no es un gasto</strong>: es la misma{' '}
              {enviando.insumo.nombre.toLowerCase()} cambiando de sitio. No toca
              el costo ni la existencia total. Se vuelve gasto el día que entra a
              una tina, no el día que se sube al carro.
            </p>

            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setEnviando(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>
                {guardando ? 'Guardando…' : '🚚 Anotar el envío'}
              </button>
            </div>
          </form>
        </div>
      )}

      {comprando && (
        <div className="bloque">
          <h2>🛒 Comprar {comprando.insumo.nombre}</h2>
          <form className="cuerpo" onSubmit={comprar}>
            <div className="campos">
              {/* Se pregunta en lo que se COMPRA. Un bulto es lo que se carga
                  del camión; los kilos son cuenta del programa. */}
              <label className="campo">
                <span>
                  Cuántos {comprando.insumo.unidad_compra
                    ? comprando.insumo.unidad_compra + 's'
                    : comprando.insumo.unidad} *
                </span>
                <input type="number" step="0.01" min="0" className="peso" required autoFocus
                       value={comprando.cantidad}
                       onChange={e => setComprando({ ...comprando, cantidad: e.target.value })} />
              </label>
              <label className="campo">
                <span>Cuánto costó en total *</span>
                <input type="number" step="1" min="0" className="peso" required
                       value={comprando.costo_total}
                       onChange={e => setComprando({ ...comprando, costo_total: e.target.value })} />
              </label>
              <label className="campo">
                <span>A quién</span>
                <input value={comprando.proveedor} placeholder="Opcional"
                       onChange={e => setComprando({ ...comprando, proveedor: e.target.value })} />
              </label>
              <label className="campo">
                <span>Fecha</span>
                <input type="date" value={comprando.fecha}
                       onChange={e => setComprando({ ...comprando, fecha: e.target.value })} />
              </label>
            </div>

            {/* La cuenta a la vista. Una conversión que pasa por detrás es una
                conversión en la que nadie confía la primera vez que el número
                no le cuadra. */}
            {comprando.insumo.unidad_compra && comprando.insumo.equivale > 0 &&
             parseFloat(comprando.cantidad) > 0 && (
              <div className="aviso">
                {peso(parseFloat(comprando.cantidad), comprando.insumo.unidad_compra)}
                {' × '}{comprando.insumo.equivale} ={' '}
                <strong>
                  {peso(enGasto(comprando.insumo, parseFloat(comprando.cantidad)),
                        comprando.insumo.unidad)}
                </strong>
                {' '}que entran a la bodega.
              </div>
            )}

            {/* Si el proveedor subió el precio, se ve aquí y no dentro de tres
                meses cuando ya se compró seis veces más caro. */}
            {comp && (
              <div className={'aviso ' + (comp.subio > 10 ? 'ojo' : '')}
                   style={{ marginTop: 16, marginBottom: 0 }}>
                Sale a <strong>{plata(comp.nuevoU)}</strong> por {comprando.insumo.unidad}.
                {comp.viejoU != null && (
                  <> Venías pagando {plata(comp.viejoU)}
                    {Math.abs(comp.subio) >= 1 && (
                      <strong style={{ color: comp.subio > 0 ? 'var(--malo)' : 'var(--bueno)' }}>
                        {' '}({comp.subio > 0 ? '+' : ''}{comp.subio.toFixed(0)}%)
                      </strong>
                    )}.
                  </>
                )}
                {comp.subio > 10 && (
                  <div style={{ marginTop: 6, fontWeight: 600 }}>
                    ⚠️ Subió más del 10%. Eso se va derecho al costo del kilo de queso.
                  </div>
                )}
              </div>
            )}

            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setComprando(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>
                {guardando ? 'Guardando…' : '💾 Registrar la compra'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* DÓNDE ESTÁ CADA COSA.
          La existencia global no contesta la pregunta del día: puede decir
          «quedan 5 bultos» estando los cinco a dos horas de camino de la ruta
          que se quedó sin sal esta mañana.

          Solo sale si de verdad hay algo repartido. Con todo en la bodega esta
          tabla repetiría la de arriba. */}
      {porCentro.length > 0 && (
        <div className="bloque">
          <h2>📍 Dónde está</h2>

          {/* Lo que se está acabando, arriba y por sitio. Un centro sin sal ya
              paró; no es lo mismo que uno con poca. */}
          {(() => {
            const avisos = [];
            for (const i of insumos) {
              for (const d of dondeFalta(i, porCentro)) {
                if (!d.esBodega || d.vacio) avisos.push({ insumo: i, ...d });
              }
            }
            if (avisos.length === 0) return null;
            return (
              <div className="aviso ojo">
                {avisos.map((a, n) => (
                  <div key={n}>
                    <strong>{a.nombre}</strong>
                    {a.vacio
                      ? ` se quedó sin ${a.insumo.nombre.toLowerCase()}.`
                      : ` le queda poco ${a.insumo.nombre.toLowerCase()}: ` +
                        `${peso(a.queda, a.insumo.unidad)}.`}
                  </div>
                ))}
              </div>
            );
          })()}

          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Insumo</th>
                  <th>Sitio</th>
                  <th style={{ textAlign: 'right' }}>Le llegó</th>
                  <th style={{ textAlign: 'right' }}>Gastó</th>
                  <th style={{ textAlign: 'right' }}>Queda</th>
                </tr>
              </thead>
              <tbody>
                {insumos.filter(i => porCentro.some(c => c.insumo_id === i.id))
                  .map(i => {
                  const sitios = porSitio(i, porCentro);
                  const ok = cuadra(i, porCentro);
                  return sitios.map((x, n) => (
                    <tr key={`${i.id}-${x.id ?? 'b'}`}>
                      <td className="recio">
                        {n === 0 ? i.nombre : ''}
                        {/* La comprobación. Si los sitios no suman la existencia
                            total, hay plata perdida en alguna cuenta, y decirlo
                            vale más que pintar una tabla bonita que no suma. */}
                        {n === 0 && !ok && (
                          <div style={{ fontSize: 11, fontWeight: 700,
                                        color: 'var(--malo)' }}>
                            ⚠️ los sitios no suman el total
                          </div>
                        )}
                      </td>
                      <td>
                        {x.esBodega ? '🏢 ' : '🏭 '}{x.nombre}
                      </td>
                      <td className="num" style={{ color: 'var(--tinta-tenue)' }}>
                        {x.enviado === null ? '—' : peso(x.enviado, i.unidad)}
                      </td>
                      <td className="num">{x.usado > 0 ? peso(x.usado, i.unidad) : '—'}</td>
                      <td className="num recio"
                          style={{ color: x.queda <= 0 ? 'var(--malo)' : 'var(--bueno)' }}>
                        {peso(x.queda, i.unidad)}
                        {i.unidad_compra && i.equivale > 0 && x.queda > 0 && (
                          <div style={{ fontSize: 11, fontWeight: 400,
                                        color: 'var(--tinta-tenue)' }}>
                            {enUnidadDeCompra(x.queda, i.equivale)} {i.unidad_compra}
                            {enUnidadDeCompra(x.queda, i.equivale) === 1 ? '' : 's'}
                          </div>
                        )}
                      </td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>

          <p style={{ fontSize: 13, color: 'var(--tinta-tenue)', margin: '10px 0 0' }}>
            Lo que está en una ruta sigue siendo plata suya. Se vuelve gasto de
            esa ruta el día que entra a una tina — y ahí sale en el costo del
            kilo de esa ruta, en 🧀 Producción.
          </p>
        </div>
      )}

      {compras.length > 0 && (
        <div className="bloque">
          <h2>🧾 Compras <span className="contador">{compras.length}</span></h2>
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Insumo</th>
                  <th style={{ textAlign: 'right' }}>Cantidad</th>
                  <th style={{ textAlign: 'right' }}>Costó</th>
                  <th style={{ textAlign: 'right' }}>Por unidad</th>
                  <th>A quién</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {compras.slice(0, 40).map(c => (
                  <tr key={c.id}>
                    <td>{fecha(c.fecha)}</td>
                    <td className="recio">{nombreDe(c.insumo_id)}</td>
                    <td className="num">{c.cantidad}</td>
                    <td className="num recio">{plata(c.costo_total)}</td>
                    <td className="num">
                      {c.cantidad > 0 ? plata(c.costo_total / c.cantidad) : '—'}
                    </td>
                    <td>{c.proveedor || '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="boton chico suave" title="Corregir"
                              style={{ marginRight: 4 }}
                              onClick={() => setCorrigiendo({
                                id: c.id, insumo: nombreDe(c.insumo_id),
                                cantidad: String(c.cantidad),
                                costo_total: String(Math.round(c.costo_total)),
                                fecha: String(c.fecha).slice(0, 10),
                                proveedor: c.proveedor || '',
                              })}>
                        ✏️
                      </button>
                      <button className="boton chico suave" title="Borrar"
                              onClick={() => borrarCompra(c)}>
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

export default Insumos;
